import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Card } from "react-bootstrap";
import { FaTrain } from "react-icons/fa";
import { useIntl } from "react-intl";
import { useStore } from "react-redux";
import { useParams } from "react-router-dom";

import { createLocalAxios } from "../../lib/api.js";
import genericTranslations from "../../utils/genericTranslations.js";
import "./VehicleWashStatusList.scss";

const VehicleWashStatusList = () => {
  const intl = useIntl();
  const { identifier } = useParams();

  const store = useStore();

  const [vehicleStatuses, setVehicleStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVehicleWashStatuses = useCallback(async () => {
    const localAxios = createLocalAxios(store.getState);

    let response;
    try {
      response = await localAxios.get(`/check/public/byRegion/${identifier}`);
    } catch (err) {
      console.error(
        `Vehicle wash statuses: fetching data failed: ${err.message}`,
      );
      setError(err.message);
    } finally {
      setIsLoading(false);
    }

    setVehicleStatuses(response.data);
    setError(null);
  }, [identifier]);

  useEffect(() => {
    fetchVehicleWashStatuses();

    const intervalId = setInterval(fetchVehicleWashStatuses, 30000);

    return () => clearInterval(intervalId);
  }, [fetchVehicleWashStatuses]);

  const dismissError = () => setError(null);

  return (
    <Card>
      <Card.Body>
        {error && (
          <Alert variant="danger" onClose={dismissError} dismissible>
            <Alert.Heading>
              {intl.formatMessage(genericTranslations.errorMessage)}
            </Alert.Heading>
            <p>{error.toString()}</p>
          </Alert>
        )}

        {isLoading && (
          <span className="align-middle">
            {intl.formatMessage(genericTranslations.loadingInProgress)}
          </span>
        )}

        {!isLoading && vehicleStatuses.length === 0 && (
          <span className="align-middle">
            {intl.formatMessage(genericTranslations.nothingFound)}
          </span>
        )}

        {!isLoading && vehicleStatuses.length > 0 && (
          <div className="vehicle-wash-status">
            {vehicleStatuses.map((vehicle) => {
              let hoursAgo = null;
              let nextCheck = null;
              let vehicleClassName = "success";
              const now = moment();
              const checkDate = vehicle.CheckType?.lastCheck?.date;
              const checkType = vehicle.CheckType;

              if (checkDate) {
                hoursAgo = moment().diff(moment(checkDate, "X"), "hours");
                if (checkType.warning === checkType.success) {
                  if (hoursAgo >= checkType.danger) {
                    vehicleClassName = "danger";
                  }
                  if (hoursAgo <= checkType.success) {
                    vehicleClassName = "success";
                  }
                } else {
                  if (hoursAgo > checkType.warning) {
                    vehicleClassName = "warning";
                  }
                  if (hoursAgo >= checkType.danger) {
                    vehicleClassName = "danger";
                  }
                  if (
                    hoursAgo <= checkType.success &&
                    hoursAgo < checkType.warning
                  ) {
                    vehicleClassName = "success";
                  }
                }
                nextCheck = moment(checkDate, "X").add(
                  checkType.danger,
                  "hours",
                );
              } else {
                vehicleClassName = "danger";
              }

              return (
                <div
                  className="vehicle-wash-status-grid-cell"
                  key={vehicle.vehicleNumber}
                >
                  <div
                    className={`vehicle-wash-status-container vehicle-wash-status-${vehicleClassName}`}
                  >
                    <div className="vehicleContainer">
                      <FaTrain className="vehicle-status-train" />
                      <h4 className="vehicle-wash-status-number">
                        {vehicle.vehicleNumber}
                      </h4>
                    </div>
                    <span className="hours">
                      {checkDate
                        ? `${Math.abs(nextCheck && nextCheck.diff(now, "hours"))} uren`
                        : "Nooit gewassen"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default VehicleWashStatusList;
